import React from 'react';
import { ChevronDown, ChevronUp, Settings } from 'lucide-react';
import { SystemPlanningConfig, SystemRecommendations, EVType } from '../../types/systemPlanning';
import { EVConfigSection } from './EVConfigSection';
import { HomeConfigSection } from './HomeConfigSection';
import { SolarBatteryConfig } from './SolarBatteryConfig';
import { RecommendationDisplay } from './RecommendationDisplay';

interface Props {
  isPanelOpen: boolean;
  togglePanel: () => void;
  config: SystemPlanningConfig;
  recommendations: SystemRecommendations;
  updateConfig: (partial: Partial<SystemPlanningConfig>) => void;
  updateEVType: (index: number, type: EVType) => void;
}

export function SystemPlanningPanel({
  isPanelOpen,
  togglePanel,
  config,
  recommendations,
  updateConfig,
  updateEVType,
}: Props) {
  const coverageColor =
    recommendations.currentRenewableCoveragePercent >= 90
      ? 'text-green-600'
      : recommendations.currentRenewableCoveragePercent >= 70
      ? 'text-yellow-600'
      : 'text-red-600';

  return (
    <div className="bg-white rounded-lg shadow-md mb-8 overflow-hidden">
      {/* Header bar — always visible */}
      <button
        onClick={togglePanel}
        className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors text-left"
      >
        <div className="flex items-center gap-3">
          <Settings size={20} className="text-indigo-600" />
          <div>
            <span className="font-semibold text-gray-800">System Planning Tool</span>
            <span className="text-sm text-gray-500 ml-2">Configure your energy system</span>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="hidden sm:flex items-center gap-3 text-sm">
            <span className="text-gray-500">
              Load: <span className="font-semibold text-gray-700">{recommendations.totalLoadKw.toFixed(1)} kW</span>
            </span>
            <span className={`font-semibold ${coverageColor}`}>
              Coverage: {recommendations.currentRenewableCoveragePercent.toFixed(0)}%
            </span>
          </div>
          {isPanelOpen ? (
            <ChevronUp size={18} className="text-gray-400" />
          ) : (
            <ChevronDown size={18} className="text-gray-400" />
          )}
        </div>
      </button>

      {/* Expandable body */}
      {isPanelOpen && (
        <div className="px-6 pb-6 border-t border-gray-100">
          <p className="text-xs text-gray-500 mt-4 mb-5">
            Adjust your system configuration and see real-time recommendations for optimal renewable coverage.
            Changes are reflected instantly in the live simulation above.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left: Config inputs */}
            <div className="space-y-6">
              <div>
                <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-3">
                  Load Configuration
                </h3>
                <div className="space-y-5">
                  <EVConfigSection
                    config={config}
                    updateConfig={updateConfig}
                    updateEVType={updateEVType}
                  />
                  <HomeConfigSection config={config} updateConfig={updateConfig} />
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-3">
                  Existing Installation
                </h3>
                <SolarBatteryConfig config={config} updateConfig={updateConfig} />
              </div>
            </div>

            {/* Right: Recommendations */}
            <div>
              <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-3">
                AI Recommendations
              </h3>
              <RecommendationDisplay recommendations={recommendations} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
